import type {
  ActivityOrigin,
  ActivityType,
  ColumnKind,
  Priority,
  ProjectStatus,
  ProjectType,
  ProjectVisibility,
} from './enums.js'

/**
 * Modelo de domínio do núcleo — projetos, quadros, colunas e atividades.
 * Ver docs/SDD/06-modulo-projetos-kanban.md
 */

export interface Member {
  readonly id: string
  /**
   * Usuário do SSO dono desta ficha. Ausente = ficha digitada à mão, sem login:
   * a pessoa não consegue entrar até ser vinculada (migration 022).
   */
  readonly userId?: string
  readonly fullName: string
  readonly email: string
  readonly initials: string
  readonly isActive: boolean
  /** Cargo organizacional. Papel ≠ cargo: cargo não concede permissão (ver roles.ts). */
  readonly positionTitle?: string
  /**
   * A quem a pessoa responde no organograma.
   * Ausente = raiz (não responde a ninguém do time). No máximo um gestor; sem ciclos (R3).
   */
  readonly managerId?: string
  readonly githubLogin?: string
  /** Base da capacidade semanal. Vazio = 40h. */
  readonly weeklyCapacityHours?: number
  readonly startedOn?: string
  readonly deactivatedAt?: string
  /** Caminho da foto no bucket privado `avatars`. Ausente = iniciais. */
  readonly avatarPath?: string
}

/** Quem trabalha ou trabalhou num projeto (view `project_participant`). */
export interface ProjectParticipant {
  readonly projectId: string
  readonly memberId: string
  readonly lastAt: string
}

export interface Project {
  readonly id: string
  readonly key: string
  readonly name: string
  readonly description?: string
  readonly type: ProjectType
  readonly status: ProjectStatus
  readonly visibility: ProjectVisibility
  /** Criticidade: `baixa` | `media` | `alta` | `critica` (etiqueta do projeto). */
  readonly color: string
  readonly leaderId?: string
  /** Quem cadastrou. Autoria edita o projeto até delegar (migration 009). */
  readonly createdBy?: string
  readonly createdAt: string
  /** Coluna do quadro de Projetos. `status` é sempre a fase dela (trigger no banco). */
  readonly stageId: string
  /** Ordem dentro da coluna — fracionária, como a posição da atividade. */
  readonly boardPosition: number
}

/**
 * Coluna do quadro de Projetos. Nome e ordem são do departamento; `status` é a
 * fase do ciclo de vida que o projeto herda ao entrar nela — é assim que
 * "Homologação" e "Desenvolvimento" coexistem sem deixar de ser `ativo` para
 * os indicadores.
 */
export interface ProjectStage {
  readonly id: string
  readonly name: string
  readonly position: number
  readonly status: ProjectStatus
}

export interface BoardColumn {
  readonly id: string
  readonly projectId: string
  readonly name: string
  readonly position: number
  readonly kind: ColumnKind
  /** Q50a — WIP é aviso, nunca bloqueio. `null` = sem limite. */
  readonly wipLimit: number | null
  /**
   * ADR-0022 (5.6): para onde o PR leva a atividade. `andamento` = PR em
   * rascunho; `revisao` = PR aberto. Só em coluna intermediária, uma de cada
   * por quadro. A final (merge) é sempre a de conclusão.
   */
  readonly flowRole?: ColumnFlowRole | null
}

export type ColumnFlowRole = 'andamento' | 'revisao'

export interface ChecklistItem {
  readonly id: string
  readonly text: string
  readonly done: boolean
}

export interface Checklist {
  readonly id: string
  readonly title: string
  /** Preenchido quando veio de um processo — a cópia registra a versão aplicada. */
  readonly templateId?: string
  readonly templateVersion?: number
  readonly items: readonly ChecklistItem[]
}

export interface Label {
  readonly id: string
  readonly name: string
  readonly color: string
}

export interface Activity {
  readonly id: string
  /** `SIS-123`. Fio de rastreabilidade até o PR e o chat. */
  readonly key: string
  readonly projectId: string
  readonly columnId: string
  /** Decimal: permite inserir entre dois cards sem reindexar a coluna. */
  readonly position: number
  readonly title: string
  readonly description?: string | undefined
  /**
   * INVIOLÁVEL (I3): exatamente um responsável.
   * `null` só é permitido enquanto a atividade está na coluna inicial (Q50b).
   */
  readonly assigneeId: string | null
  // `| undefined` explícito: com exactOptionalPropertyTypes, é o que permite
  // LIMPAR a data (voltar a "sem prazo"), não só omiti-la na criação.
  readonly startOn?: string | undefined
  readonly dueOn?: string | undefined
  readonly completedAt?: string | undefined
  /** Soft-delete (migration 012). Presente = fora do quadro e das listas; o histórico fica. */
  readonly archivedAt?: string | undefined
  /** 0–10. `null` até ser avaliada. Congela ao sair da coluna inicial (Q44). */
  readonly difficulty: number | null
  readonly difficultyFrozen: boolean
  readonly priority: Priority
  readonly type: ActivityType
  readonly origin: ActivityOrigin
  readonly labelIds: readonly string[]
  readonly checklists: readonly Checklist[]
  readonly createdAt: string
  readonly updatedAt: string
  /** Quem criou. Também pode mandá-la para a Lixeira (ADR-0017). */
  readonly createdBy?: string | undefined
}

/** Evento imutável de atividade (I4). Base de todo indicador de tempo. */
export interface ActivityEvent {
  readonly id: string
  readonly activityId: string
  readonly kind:
    | 'criada'
    | 'movida'
    | 'responsavel_alterado'
    | 'prazo_alterado'
    | 'dificuldade_definida'
    | 'concluida'
    | 'reaberta'
  readonly from?: string
  readonly to?: string
  readonly authorId: string
  readonly occurredAt: string
}

export interface ProcessTemplate {
  readonly id: string
  readonly name: string
  readonly category?: string
  readonly version: number
  readonly isActive: boolean
  readonly items: readonly string[]
}

// --- Regras de domínio — funções puras, testáveis sem banco -----------------

export const DIFFICULTY_MIN = 0
export const DIFFICULTY_MAX = 10

export function isValidDifficulty(value: number): boolean {
  return (
    Number.isInteger(value) && value >= DIFFICULTY_MIN && value <= DIFFICULTY_MAX
  )
}

/** Prazo nunca antes do início. */
export function isValidSchedule(startOn?: string, dueOn?: string): boolean {
  if (!startOn || !dueOn) return true
  return dueOn >= startOn
}

export type MoveRejection =
  | { readonly ok: true }
  | { readonly ok: false; readonly reason: string }

/**
 * Q50b — atividade sem responsável só existe na coluna inicial.
 * Sair do backlog exige alguém accountable.
 */
export function canMoveActivity(
  activity: Pick<Activity, 'assigneeId'>,
  target: Pick<BoardColumn, 'kind' | 'name'>,
): MoveRejection {
  if (target.kind !== 'inicial' && activity.assigneeId === null) {
    return {
      ok: false,
      reason: 'Defina um responsável antes de tirar a atividade do backlog.',
    }
  }
  return { ok: true }
}

/** Q50a — WIP avisa, não impede. */
export function wipStatus(
  column: Pick<BoardColumn, 'wipLimit'>,
  currentCount: number,
): 'ok' | 'no-limite' | 'excedido' {
  if (column.wipLimit === null) return 'ok'
  if (currentCount > column.wipLimit) return 'excedido'
  if (currentCount === column.wipLimit) return 'no-limite'
  return 'ok'
}

/**
 * Posição decimal entre dois cards. Inserir no meio não reindexa a coluna.
 * `before`/`after` ausentes significam topo/fundo.
 */
export function positionBetween(before?: number, after?: number): number {
  if (before === undefined && after === undefined) return 1000
  if (before === undefined) return (after as number) / 2
  if (after === undefined) return before + 1000
  return (before + after) / 2
}

export type DueState =
  | 'sem-prazo'
  | 'atrasada'
  | 'hoje'
  | 'proxima'
  | 'tranquila'
  /** Concluída até o dia do prazo. */
  | 'entregue-no-prazo'
  /** Concluída depois do prazo: o atraso ficou na história, mas não é mais pendência. */
  | 'entregue-com-atraso'

/** Dia (YYYY-MM-DD) de um instante no fuso do departamento. */
export function localDateOf(instant: string, timeZone = 'America/Sao_Paulo'): string {
  return new Date(instant).toLocaleDateString('sv-SE', { timeZone })
}

/**
 * Estado do prazo para colorir o card. `today` em ISO (YYYY-MM-DD).
 *
 * Concluída não está atrasada: compara o dia da conclusão com o prazo, não
 * hoje (queixa do dono, 29/09: "foi para concluída dentro do prazo e segue
 * atrasada").
 */
export function dueState(dueOn: string | undefined, today: string, completedAt?: string | null): DueState {
  if (completedAt) {
    if (!dueOn) return 'sem-prazo'
    return localDateOf(completedAt) <= dueOn ? 'entregue-no-prazo' : 'entregue-com-atraso'
  }
  if (!dueOn) return 'sem-prazo'
  if (dueOn < today) return 'atrasada'
  if (dueOn === today) return 'hoje'

  const diffDays =
    (Date.parse(`${dueOn}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000
  return diffDays <= 3 ? 'proxima' : 'tranquila'
}

export function checklistProgress(
  checklists: readonly Checklist[],
): { done: number; total: number } {
  let done = 0
  let total = 0
  for (const list of checklists) {
    for (const item of list.items) {
      total += 1
      if (item.done) done += 1
    }
  }
  return { done, total }
}

/**
 * Cadastro mínimo para sair da coluna Rascunhos.
 * Tag pública = nome do repo; `key` interna não entra na regra.
 */
export function isProjectCadastroComplete(
  project: Pick<Project, 'name' | 'description' | 'type' | 'color' | 'leaderId'>,
  hasRepository: boolean,
): boolean {
  return (
    project.name.trim().length > 0 &&
    Boolean(project.description?.trim()) &&
    Boolean(project.type) &&
    Boolean(project.color) &&
    Boolean(project.leaderId) &&
    hasRepository
  )
}

/**
 * Trava D21 aplicada ao quadro de etapas: rascunho só entra numa coluna fora
 * da fase Rascunho com o cadastro completo. Entre colunas da mesma fase, ou
 * de volta para Rascunho, o movimento é livre.
 */
export function canEnterStage(
  project: Pick<Project, 'status' | 'name' | 'description' | 'type' | 'color' | 'leaderId'>,
  stage: Pick<ProjectStage, 'status'>,
  hasRepository: boolean,
): boolean {
  if (project.status !== 'rascunho' || stage.status === 'rascunho') return true
  return isProjectCadastroComplete(project, hasRepository)
}

/**
 * Gera chave interna 2–6 letras a partir do nome (não é a tag da UI).
 * Garante unicidade contra `taken`.
 */
export function suggestProjectKey(name: string, taken: readonly string[]): string {
  const letters = name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Za-z]/g, '')
    .toUpperCase()

  const words = name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .split(/[^A-Za-z]+/)
    .filter(Boolean)

  const fromWords = words
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('')
    .slice(0, 6)

  let base = (fromWords.length >= 2 ? fromWords : letters.slice(0, 4)) || 'PROJ'
  if (base.length < 2) base = `${base}XX`.slice(0, 2)
  base = base.slice(0, 6)

  const takenSet = new Set(taken.map((k) => k.toUpperCase()))
  if (!takenSet.has(base)) return base

  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  for (const ch of alphabet) {
    const candidate = `${base.slice(0, 5)}${ch}`.slice(0, 6)
    if (candidate.length >= 2 && !takenSet.has(candidate)) return candidate
  }
  for (const a of alphabet) {
    for (const b of alphabet) {
      const candidate = `${base.slice(0, 4)}${a}${b}`.slice(0, 6)
      if (!takenSet.has(candidate)) return candidate
    }
  }
  return 'ZZZZZZ'
}

type MemberHierarchy = { readonly id: string; readonly managerId?: string }

/**
 * R3 — organograma é árvore: no máximo um gestor, sem auto-referência e sem ciclos.
 * `managerId` vazio/`undefined` = raiz (não responde a ninguém).
 */
export function validateManagerAssignment(
  members: readonly MemberHierarchy[],
  memberId: string,
  managerId: string | undefined,
): { ok: true } | { ok: false; reason: string } {
  if (!managerId) return { ok: true }
  if (managerId === memberId) {
    return { ok: false, reason: 'Uma pessoa não pode responder a si mesma.' }
  }
  const byId = new Map(members.map((m) => [m.id, m]))
  if (!byId.has(managerId)) {
    return { ok: false, reason: 'Gestor selecionado não existe no time.' }
  }

  // Sobe a cadeia a partir do gestor proposto: se encontrar o próprio membro, há ciclo.
  const seen = new Set<string>([memberId])
  let cursor: string | undefined = managerId
  while (cursor) {
    if (seen.has(cursor)) {
      return {
        ok: false,
        reason: 'Essa subordinação fecharia um ciclo no organograma.',
      }
    }
    seen.add(cursor)
    cursor = byId.get(cursor)?.managerId
  }
  return { ok: true }
}
